/* eslint-disable dot-notation */
/* eslint-disable camelcase */
import { sendDocument } from '@/api/document/index'
import { authGetters } from '@/store/modules/auth/routines'
import {
  documentTypesDic,
  userWhitelabelGroupCookieName,
  gclidCookieName,
  subdomainList,
  utmTrackingCookies,
  legacyDynamicForms,
  revenueThresholdsByCountry,
  fundingDocuments
} from '@/utils/constants'
import { onboardingGetters } from '@/store/modules/onboarding/routines'
import store from '@/store/index'
import { removeSeparatorsAndParseToNumber } from '@/utils/currency'

export const isPromise = function (obj) {
  return !!obj && (typeof obj === 'object' || typeof obj === 'function') && typeof obj.then === 'function'
}

export const camelToKebab = function (string) {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export const decodeJWT = token => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace('-', '+').replace('_', '/')
  return JSON.parse(window.atob(base64))
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const determineEnvironment = () => {
  let currentUrl = window.location.href
  let qaIndicators = ['.stage.', '.uat.', '.dev.', 'localhost']
  return qaIndicators.some(indicator => currentUrl.includes(indicator)) ? 'qa' : 'prod'
}

export const escapeRegExp = str => str.replace(/[\\^$*+?.()|[\]{}]/g, '\\$&')

export const downloadFile = (data, filename, mime) => {
  const blob = new Blob([data], { type: mime || 'application/octet-stream' })
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    window.navigator.msSaveBlob(blob, filename)
  } else {
    const blobURL = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobURL
    link.setAttribute('download', filename)
    link.setAttribute('hidden', true)

    if (typeof link.download === 'undefined') {
      link.setAttribute('target', '_blank')
    }

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(blobURL)
  }
}

export const formatDate = date => new Intl.DateTimeFormat('en-GB').format(new Date(date))

export const getCookie = name => {
  const matches = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`))
  return matches ? decodeURIComponent(matches[1]) || null : null
}

export const getDomainForCookie = () => window.location.hostname.substring(window.location.hostname.indexOf('.swoopfunding') + 1)

export const getWhitelabelGroup = (subdomain = getSubdomain()) => {
  /**
   * Returns the name of the whitelabel group it belongs to or just the subdomain.
   * For example bedfordshire-chamber-of-commerce would return bcc here.
   */
  if (subdomain === 'swoop') return subdomain
  return Object.keys(subdomainList).find(key => subdomainList[key].includes(subdomain)) || subdomain
}

export const getSubdomain = () => {
  /**
   * Returns the hostname directly from the url.
   * Works for localhost and converts 'app' subdomain name to 'swoop'
   */
  const subdomain = window.location.hostname.split('.').shift()
  if (subdomain === 'localhost') return import.meta.env.VITE_WHITELABEL_SUBDOMAIN || 'swoop'
  else if (subdomain === 'app') return 'swoop'

  return subdomain
}

export const getSubdomainReferralSource = (subdomain = getSubdomain()) => {
  return ['app', 'swoop'].includes(subdomain) ? 'Direct' : subdomain
}

export const setUserWhitelabelGroup = whitelabelGroup => {
  const maxAge = whitelabelGroup ? 365 * 24 * 60 * 60 : 0
  const domain = getDomainForCookie()
  document.cookie = `${userWhitelabelGroupCookieName}=${whitelabelGroup}; max-age=${maxAge}; domain=${domain}; samesite=lax; path=/; secure`
}
export const deleteCookie = cookieName => {
  const domain = getDomainForCookie()
  document.cookie = cookieName + `=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${domain};`
}

export const getOfficerName = officer => officer.fullName || `Unnamed Officer: ${officer.officerId}`

export const getDocumentNameByCategoryAndTitle = (name = '', category = '', title = '') => {
  const fileName = `${documentTypesDic[category] || category}-${title}-${name}`
  return fileName
}

export const convertToDateString = function (date) {
  return new Date(date).toDateString()
}

export function requiredDocumentsAdapter({ documentsList, requiredDocuments, isPrivateLimitedCompany, opportunity, taxation, countryCode }) {
  const isUSorCA = ['US', 'CA'].includes(countryCode)

  const productRequiredDocumentsDic = {
    hasBankStatements: {
      3: { label: 'Bank Statements', type: 'Other', title: '3 Months Bank Statements' },
      6: { label: 'Bank Statements', type: 'Financials', title: '6 Months Company Bank Statements' },
      12: { label: 'Bank Statements', type: 'Financials', title: '12 Months Bank Statements' }
    },
    filedAccounts: {
      1: { title: '1 Year Filed Accounts', type: 'Financials', label: 'Filed Accounts' },
      2: { title: '2 Years Filed Accounts', type: 'Financials', label: 'Filed Accounts' }
    },
    filedAuditAccounts: { title: '1 Year Filed Accounts', type: 'Financials', label: 'Filed Accounts' },
    proofOfIdentity: { label: 'Proof of Identity', title: 'Proof of Identity', type: 'Identification' },
    proofOfAddress: { label: 'Proof of Address (Dated within last 3 months)', title: 'Proof of Address (Dated within last 3 months)', type: 'Identification' },
    hasManagementAccounts: {
      label: isUSorCA ? 'Financial Statements' : 'Management Accounts',
      type: 'Financials',
      title: isUSorCA ? 'Financial Statements' : 'Management Accounts'
    },
    hasVATReturns: { label: `${taxation} Returns`, type: 'Financials', title: `${taxation} Returns` },
    hasPersonalGuarantee: { label: 'I confirm I am willing to offer a Personal Guarantee', type: 'Other', title: 'Personal Guarantee' },
    isSummaryAgedDebtors: { label: 'Summary Aged Debtors report', type: 'Financials', title: 'Summary Aged Debtors report' },
    isDetailedAgedDebtors: { label: 'Detailed Aged Debtors report', type: 'Financials', title: 'Detailed Aged Debtors report' },
    isSummaryAgedCreditors: { label: 'Summary Aged Creditors report', type: 'Financials', title: 'Summary Aged Creditors report' },
    isDetailedAgedCreditors: { label: 'Detailed Aged Creditors report', type: 'Financials', title: 'Detailed Aged Creditors report' },
    hasBusinessPlan: { label: 'Business Plan', type: 'FundingDocuments', title: 'A Business Plan' },
    hasPitchDeck: { label: 'Pitch Deck', type: 'FundingDocuments', title: 'Pitch Deck' },
    hasFinancialModel: { label: 'Financial Model', type: 'FundingDocuments', title: 'A Financial Model' },
    hasFinancialForecast: { label: 'Financial Forecast', type: 'FundingDocuments', title: 'Financial Forecast' },
    hasSalie: {
      label: 'Statement of Assets and Liabilities and Income and Expenditure',
      type: 'Financials',
      title: 'Statement of Assets and Liabilities and Income and Expenditure'
    },
    cashFlowForecast: { label: 'Cashflow Forecast', type: 'Financials', title: 'Cashflow Forecast' },
    priorYearFinancialStatements: {
      label: 'Prior year financial statements',
      type: 'Financials',
      title: 'Prior year financial statements'
    },
    yearToDateFinancialStatements: {
      label: 'YTD financial statements',
      type: 'Financials',
      title: 'YTD financial statements'
    },
    additionalDocuments: {
      label: 'Additional Documents',
      type: 'Other',
      title: 'Additional Documents'
    }
  }

  return Object.entries(requiredDocuments)
    .filter(([key, value]) => {
      let isRequired
      switch (key) {
        case 'hasPersonalGuarantee':
          isRequired = value === 'Yes' || (value === 'YesForPrivateLimitedCompany' && isPrivateLimitedCompany)
          break
        case 'hasBusinessPlan ':
          // For Equity must be always true
          isRequired = opportunity.type === 'Grant' ? true : value
          break
        case 'hasPitchDeck ':
          // For Equity must be always true
          isRequired = opportunity.type === 'Equity' ? true : value
          break
        default:
          isRequired = value
      }
      return isRequired && key in productRequiredDocumentsDic
    })
    .map(([key, value]) => {
      let document
      switch (key) {
        case 'hasPersonalGuarantee':
          document = {
            ...productRequiredDocumentsDic[key],
            isCheckBox: true
          }
          break
        case 'hasBankStatements':
          document = productRequiredDocumentsDic[key][opportunity?.bankStatements || 12]
          break
        case 'filedAccounts':
          document = productRequiredDocumentsDic[key][value]
          break
        default:
          document = productRequiredDocumentsDic[key]
      }
      // remove personal guarantee from the required documents as it's a checkbox and isn't recorded by the server.
      const uploadedDocuments = [...documentsList, { title: 'Personal Guarantee', type: 'Other' }].filter(doc => doc.title === document.title && doc.type === document.type)
      const uploadedDocumentNames = uploadedDocuments.map(doc => doc.name)
      const isDocumentUploaded = uploadedDocuments.length > 0
      return {
        ...document,
        key,
        uploadedDocumentNames,
        isDocumentUploaded
      }
    })
}

export function getGclid() {
  const cookieValue = getCookie(gclidCookieName) || ''
  const [, , gclid = null] = cookieValue.split('.')
  return gclid
}

export const base64ToSVG = base64 => {
  const name = 'logo.svg'
  const arr = base64.split(',')
  const mime = 'image/svg'
  const bstr = window.atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], name, {
    type: mime
  })
}

export const mergeObject = (obj, objToUpdate) => {
  // nested Logic used to update each individual property regardless of object depth.
  // mutates the second object directly.

  Object.keys(obj).forEach(key => {
    if (typeof objToUpdate[key] === 'undefined') return console.log(`Failed to replace ${key} config value`)

    if (Array.isArray(obj[key]) || typeof obj[key] !== 'object') {
      objToUpdate[key] = obj[key]
      return
    }

    return mergeObject(obj[key], objToUpdate[key])
  })
}

export const getNextDeeplinkRoute = () => {
  const deepLinkJourney = JSON.parse(window.localStorage.getItem('deepLinkJourney')) || null
  if (deepLinkJourney && deepLinkJourney.length > 0) {
    return deepLinkJourney[0]
  }
  return null
}

export const natwestGetLombardLink = group => {
  switch (group) {
    case 'rbs':
      return 'https://quote.lombard.co.uk/landing/cars-and-vans/?intcam=LMB_SF_RBS'
    case 'ulster':
      return 'https://quote.lombard.co.uk/landing/cars-and-vans/?intcam=LMB_SF_UB'
    case 'lombard':
      return 'https://quote.lombard.co.uk/landing/cars-and-vans/?intcam=LMB_SF_L'
    default:
      return 'https://quote.lombard.co.uk/landing/cars-and-vans/?intcam=LMB_SF_NW'
  }
}

export const getLockedProducts = goalsCompleted => {
  if (!goalsCompleted || goalsCompleted.length < 1) goalsCompleted = []
  const completedGoalsTypes = goalsCompleted.map(e => e.type)
  return {
    loansLocked: false,
    equityLocked: !completedGoalsTypes.includes('Equity'),
    grantsLocked: false,
    mortgageLocked: !completedGoalsTypes.includes('Mortgage')
  }
}

export const convertBase64ToImage = (base64, name) => {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = window.atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], name, {
    type: mime
  })
}

export const uploadFormFile = async (companyId, fileToUploadObject, fileName) => {
  if (!companyId || !fileToUploadObject) return

  const formData = new FormData()
  formData.append('formFile', fileToUploadObject.file, fileName)

  const titleToSend = fileToUploadObject.fieldName || 'Pitch Deck'

  // TODO: This needs to be updated if we add any other file uploads in dynamic forms
  const response = await sendDocument(formData, {
    companyId: companyId,
    type: 'FundingDocuments',
    title: titleToSend
  })

  return {
    documentId: response.data.documentId,
    fieldId: fileToUploadObject.fieldId
  }
}

export const setFormFilePayload = (form, formFile) => {
  if (!formFile) return form
  // Deep copy the form object so that we don't mutate state directly
  form = JSON.parse(JSON.stringify(form))

  form.sections.forEach(section => {
    section.fieldValues.forEach(field => {
      if (field.fieldId === formFile.fieldId) {
        const newFieldProps = field.value ? JSON.parse(field.value) : null
        if (newFieldProps) {
          field.value = JSON.stringify({
            name: newFieldProps.name,
            documentId: formFile.documentId
          })
        }
      }
    })
  })

  return form
}

export const getUtmParameters = () => {
  const params = {}

  document.cookie.split('; ').forEach(cookie => {
    const [key, value] = cookie.split('=')

    if (utmTrackingCookies?.includes(key)) {
      params[key.replace(/utm_/gi, '')] = decodeURIComponent(value)
    }
  })
  return params
}

export const setCookie = (key, value, daysTillExpires = 1) => {
  const domain = getDomainForCookie()
  const now = new Date()
  const expires = new Date(now.setDate(now.getDate() + 1))
  if (value.includes('/')) {
    value = encodeURIComponent(value)
  }

  document.cookie = `${key}=${value}; expires=${expires.toUTCString()}; domain=${domain}; path=/;`
}

export const getURLParams = () => {
  const urlParams = window.location.href.split('?')[1] || null
  const params = {}
  if (!urlParams) return

  try {
    if (btoa(atob(urlParams)) === urlParams) {
      atob(urlParams)
        .split('&')
        .forEach(param => {
          const [key, value] = param.split('=')
          params[key] = decodeURIComponent(value)
        })

      return params
    }
  } catch (e) {
    urlParams.split('&').forEach(param => {
      const [key, value] = param.split('=')
      if (utmTrackingCookies.includes(key)) {
        params[key] = value
      }
    })
    return params
  }
}

export const setUTMCookiesFromURL = () => {
  const urlCookies = getURLParams()

  if (!urlCookies) return

  Object.keys(urlCookies).forEach(key => {
    if (utmTrackingCookies.includes(key)) {
      setCookie(key, urlCookies[key])
    }
  })
}

export const pageTitleInTitleCase = () => {
  // Router converts route name to title case and adds ' - Swoop'
  // By just removing the ' - Swoop' we don't have to rewrite the logic
  if (document.title && document.title.includes(' - ')) {
    return document.title.split(' - ')[0]
  }

  // Fallback to get pageTitle from pathname
  const pathName = window.location.pathname.slice(1)

  if (pathName.includes('/') || pathName.includes('-')) {
    return pathName
      .split(/[-/]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  } else {
    return pathName.charAt(0).toUpperCase() + pathName.slice(1)
  }
}

export const getSourceType = (defaultValue = 'Direct', utmParams = getUtmParameters()) => {
  const http_referer = utmParams['http_referer']
  const utm_source = utmParams.source
  let lead_source

  switch (utmParams.medium) {
    case 'cpc':
      if (utm_source === 'google') {
        lead_source = 'Google Ads'
      } else if (utm_source === 'bing') {
        lead_source = 'Bing Ads'
      } else {
        lead_source = 'Paid Search'
      }
      break
    case 'referral':
      lead_source = 'Referral'
      break
    case 'email':
      lead_source = 'Email'
      break
    default:
      if (!http_referer) {
        lead_source = defaultValue
      } else {
        if (http_referer.includes('google') || http_referer.includes('bing') || http_referer.includes('yahoo') || http_referer.includes('duckduckgo')) {
          lead_source = 'SEO lead'
        } else if (http_referer.includes('swoopfunding.com')) {
          lead_source = defaultValue
        } else if (
          http_referer.includes('linkedin') ||
          http_referer.includes('facebook') ||
          http_referer.includes('twitter') ||
          http_referer.includes('glassdoor') ||
          http_referer.includes('youtube')
        ) {
          lead_source = 'Social'
        } else {
          lead_source = 'Referral'
        }
      }
      break
  }
  return lead_source
}

export const getGenericTrackingProperties = () => {
  const params = getUtmParameters()
  const url = window.location.href
  const user = store.getters[authGetters.USER]
  let fundingReason

  if (user && user.goalsCompleted?.length > 0) {
    fundingReason = user.goalsCompleted[user.goalsCompleted.length - 1]?.formName ?? null
  } else {
    fundingReason = store.getters[onboardingGetters.FUNDING_REASON_FORM_INFO]?.fundingReason ?? null
  }

  return {
    fundingReason,
    pageTitle: pageTitleInTitleCase(),
    sourceType: getSourceType('Direct', params),
    domain: url.split('.')[0],
    current_url: url,
    utm_source: params?.source || null,
    utm_medium: params?.medium || null,
    utm_campaign: params?.campaign || null,
    utm_content: params?.content || null,
    utm_term: params?.term || null,
    http_referer: params?.http_referer || getSubdomainReferralSource()
  }
}

export const legacyFormRedirect = legacyFormRoute => {
  const fundingReason = legacyDynamicForms.find(form => form.redirect === legacyFormRoute)?.goalName ?? null

  if (fundingReason) {
    return { name: 'get-started', query: { fundingReason: fundingReason } }
  }

  return '/'
}

export const formatSectorsToLabels = fieldValue => {
  if (typeof fieldValue === 'string' && fieldValue.trim().startsWith('[')) {
    try {
      const parsedSectors = JSON.parse(fieldValue)
      if (Array.isArray(parsedSectors)) {
        return parsedSectors.map(item => item.label).join(', ')
      }
    } catch (e) {
      console.error('Failed to parse sectors JSON:', e)
    }
  }
  return fieldValue
}

export const convertDynamicFormValuesForTracking = (fieldName, fieldValue) => {
  // 1) Booleans
  if (fieldValue === 'true') return true
  if (fieldValue === 'false') return false

  // 2) Fields we do NOT transform
  if (fieldName === 'contactNumber' || fieldName === 'tradingStartDate') {
    return fieldValue
  }

  // 3) Sectors
  if (fieldName === 'sectors') {
    return formatSectorsToLabels(fieldValue)
  }

  // Guard against UUID/ID truncation
  const isIdField = typeof fieldName === 'string' && fieldName.toLowerCase().endsWith('id')
  const isUuidLike = typeof fieldValue === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldValue.trim())

  // If it’s an ID field or UUID string, return as-is (don't parse to number)
  if (isIdField || isUuidLike) {
    return fieldValue
  }

  // 4) Only try numeric parsing for strings
  if (typeof fieldValue === 'string') {
    const parsedValue = removeSeparatorsAndParseToNumber(fieldValue)
    if (!Number.isNaN(parsedValue)) {
      return parsedValue
    }
  }

  // 5) Preserve arrays
  if (Array.isArray(fieldValue)) {
    return fieldValue
  }

  // 6) Stringify objects
  if (typeof fieldValue === 'object' && fieldValue !== null) {
    return JSON.stringify(fieldValue)
  }

  return fieldValue
}

export const findSectionObjectByFieldName = (formData, fieldName) => {
  for (const item of formData) {
    if (item.name === fieldName) {
      return item
    }
    if (item.formFields) {
      const found = item.formFields.find(field => field.name === fieldName)
      if (found) {
        return found
      }
    }
  }
  return null
}

export const convertStringToCamelCase = sentence => {
  return sentence
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
      return index === 0 ? match.toLowerCase() : match.toUpperCase()
    })
    .replace(/\s+/g, '')
}

export const hasValidRegistrationNumber = registrationNumber =>
  (typeof registrationNumber === 'string' && registrationNumber.trim().length > 0) || (typeof registrationNumber === 'number' && registrationNumber > 0)

export const getRevenueThreshold = incorporationCountry => {
  const threshold = revenueThresholdsByCountry[incorporationCountry]
  return threshold === undefined ? null : threshold
}

export const hasRequiredFundingDocuments = documents =>
  [fundingDocuments.bankStatements.title, fundingDocuments.filedAccounts.title].every(title => documents.some(document => document.title === title))

export const isCompanyYoungerThan = (incorporationDate, years = 1) => {
  if (!incorporationDate) return false

  const inputDate = new Date(incorporationDate)
  if (isNaN(inputDate.getTime())) return false

  const thresholdDate = new Date()
  thresholdDate.setFullYear(thresholdDate.getFullYear() - years)

  return inputDate > thresholdDate
}

export const formatStringForDisplay = string => {
  // Insert space before capital letters unless preceded by another capital (to preserve acronyms)
  const spaced = string.replace(/(?!^)(?<![A-Z])([A-Z])/g, ' $1')

  // Make the entire string lowercase, except the first letter
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

export const getInitials = (name = '') =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('')
